import { useState, useRef, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { Plus, Trash2, ChevronDown } from 'lucide-react';
import { cn } from '@/lib/utils';
import type { ShipmentItem } from '@/lib/types';
import { useUser } from '@/contexts/UserContext';
import { useLanguage } from '@/contexts/LanguageContext';

interface MultiItemShipmentFormProps {
  customers: any[];
  trips: any[];
  warehouses: any[];
  onSubmit: (data: any) => void;
  isSubmitting?: boolean;
  showTripSelection?: boolean; // For admin/warehouse (with trip) vs supplier (without trip)
  defaultStatus?: string;       // Default status for the shipment
  initialData?: any;            // For editing existing shipments
}

interface FormItem {
  id: string;
  description: string;
  quantity: string;
  quantityUnit: string;
  weight: string;
  volume: string;
  boxes: string;
}

export function MultiItemShipmentForm({
  customers,
  trips,
  warehouses,
  onSubmit,
  isSubmitting = false,
  showTripSelection = true,
  defaultStatus = 'Processing',
  initialData
}: MultiItemShipmentFormProps) {
  const { user } = useUser();
  const { t } = useLanguage();

  // Form state - populate from initialData if editing
  const [formData, setFormData] = useState({
    customerCode: initialData?.customerCode || '',
    customerName: initialData?.customerName || '',
    originWarehouse: initialData?.originWarehouse || '',
    destinationWarehouse: initialData?.destinationWarehouse || '',
    tripName: initialData?.tripName || '',
    freightMethod: (initialData?.freightMethod || 'Air') as 'Air' | 'Sea',
    notes: initialData?.notes || ''
  });

  // Items state - populate from initialData if editing
  const [items, setItems] = useState<FormItem[]>(() => {
    if (initialData?.items && initialData.items.length > 0) {
      return initialData.items.map((item: any, idx: number) => ({
        id: String(idx + 1),
        description: item.description || '',
        quantity: String(item.quantity || ''),
        quantityUnit: item.quantityUnit || 'pcs',
        weight: String(item.weight || ''),
        volume: String(item.volume || ''),
        boxes: String(item.boxes || '')
      }));
    }
    return [
      {
        id: '1',
        description: '',
        quantity: '',
        quantityUnit: 'pcs',
        weight: '',
        volume: '',
        boxes: ''
      }
    ];
  });

  // Customer search states
  const [showCustomerDropdown, setShowCustomerDropdown] = useState(false);
  const [filteredCustomers, setFilteredCustomers] = useState<any[]>([]);
  const [selectedCustomer, setSelectedCustomer] = useState<any>(null);
  const customerDropdownRef = useRef<HTMLDivElement>(null);

  // Calculate totals from items
  const totals = items.reduce((acc, item) => ({
    weight: acc.weight + (parseFloat(item.weight) || 0),
    volume: acc.volume + (parseFloat(item.volume) || 0),
    boxes: acc.boxes + (parseInt(item.boxes) || 0)
  }), { weight: 0, volume: 0, boxes: 0 });

  // Add new item
  const handleAddItem = () => {
    const newId = String(items.length + 1);
    setItems([...items, {
      id: newId,
      description: '',
      quantity: '',
      quantityUnit: 'pcs',
      weight: '',
      volume: '',
      boxes: ''
    }]);
  };

  // Remove item
  const handleRemoveItem = (id: string) => {
    if (items.length === 1) return; // Keep at least one item
    setItems(items.filter(item => item.id !== id));
  };

  // Update item
  const handleUpdateItem = (id: string, field: keyof FormItem, value: string) => {
    setItems(items.map(item =>
      item.id === id ? { ...item, [field]: value } : item
    ));
  };

  // Customer search handler
  const handleCustomerCodeChange = (code: string) => {
    if (code.length === 0) {
      setShowCustomerDropdown(false);
      setFilteredCustomers([]);
      setSelectedCustomer(null);
      setFormData({ ...formData, customerCode: '', customerName: '' });
      return;
    }

    // Filter customers based on warehouse access
    const accessibleCustomers = user?.role === 'admin'
      ? customers
      : customers.filter(customer =>
        customer.destination_warehouse === user?.warehouseCode ||
        customer.destination_warehouse === user?.warehouseName
      );

    const matchingCustomers = accessibleCustomers.filter(customer =>
      customer.code.toLowerCase().startsWith(code.toLowerCase())
    );

    setFilteredCustomers(matchingCustomers);
    setShowCustomerDropdown(matchingCustomers.length > 0);

    const exactMatch = matchingCustomers.find(customer => customer.code === code);
    if (exactMatch) {
      setSelectedCustomer(exactMatch);
      setFormData({ ...formData, customerCode: code, customerName: exactMatch.name });
      setShowCustomerDropdown(false);
    } else {
      setSelectedCustomer(null);
      setFormData({ ...formData, customerCode: code, customerName: '' });
    }
  };

  // Handle customer selection from dropdown
  const handleCustomerSelect = (customer: any) => {
    setSelectedCustomer(customer);
    setFormData({
      ...formData,
      customerCode: customer.code,
      customerName: customer.name
    });
    setShowCustomerDropdown(false);
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (customerDropdownRef.current && !customerDropdownRef.current.contains(event.target as Node)) {
        setShowCustomerDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Handle trip selection
  const handleTripChange = (tripName: string) => {
    const trip = trips.find(t => t.name === tripName);
    if (trip) {
      setFormData({
        ...formData,
        tripName: tripName,
        originWarehouse: trip.originWarehouse || trip.origin || '',
        destinationWarehouse: trip.destinationWarehouse || trip.destination || '',
        freightMethod: trip.freightMethod || 'Air'
      });
    } else {
      setFormData({ ...formData, tripName: tripName });
    }
  };

  // Get available trips (only Planning status)
  const getAvailableTrips = () => {
    return trips.filter((trip: any) =>
      trip.status === 'Planning' &&
      (user?.role === 'admin' ||
        trip.destinationWarehouse === user?.warehouseCode ||
        trip.destinationWarehouse === user?.warehouseName ||
        trip.originWarehouse === user?.warehouseCode ||
        trip.originWarehouse === user?.warehouseName)
    );
  };

  // Get status color
  const getStatusColor = (status: string) => {
    switch (status) {
      case 'Planning': return 'bg-gray-100 text-gray-800';
      case 'In Transit': return 'bg-blue-100 text-blue-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  // Handle form submission
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    // Validate
    if (!formData.customerCode || !formData.customerName) {
      alert('Please select a customer');
      return;
    }

    if (showTripSelection && !formData.tripName) {
      alert('Please select a trip');
      return;
    }

    if (!showTripSelection && !formData.freightMethod) {
      alert('Please select a freight method');
      return;
    }

    // Validate items
    const validItems = items.filter(item => item.description.trim() !== '');
    if (validItems.length === 0) {
      alert('Please add at least one item with a description');
      return;
    }

    // Prepare shipment items data
    const shipmentItems = validItems.map((item, index) => ({
      item_number: index + 1,
      description: item.description,
      quantity: parseInt(item.quantity) || 0,
      quantity_unit: item.quantityUnit,
      weight: parseFloat(item.weight) || 0,
      volume: parseFloat(item.volume) || 0,
      boxes: parseInt(item.boxes) || 0
    }));

    // Submit data
    onSubmit({
      customer_code: formData.customerCode,
      customer_name: formData.customerName,
      origin_warehouse: formData.originWarehouse,
      destination_warehouse: formData.destinationWarehouse,
      trip_name: showTripSelection ? formData.tripName : '',
      freight_method: formData.freightMethod,
      weight: totals.weight,
      volume: totals.volume,
      packages: totals.boxes,
      status: defaultStatus,
      description: formData.notes,
      items: shipmentItems,
      estimated_delivery: new Date(Date.now() + 5 * 24 * 60 * 60 * 1000).toISOString().split('T')[0]
    });
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {/* Customer Selection */}
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label htmlFor="customerCode">Customer Code *</Label>
          <div className="relative" ref={customerDropdownRef}>
            <Input
              id="customerCode"
              value={formData.customerCode}
              onChange={(e) => handleCustomerCodeChange(e.target.value)}
              placeholder="4-digit code"
              maxLength={4}
              required
              onFocus={() => {
                if (formData.customerCode.length > 0) {
                  setShowCustomerDropdown(true);
                }
              }}
            />
            {showCustomerDropdown && filteredCustomers.length > 0 && (
              <div className="absolute z-10 w-full mt-1 bg-white border border-gray-200 rounded-md shadow-lg max-h-60 overflow-y-auto">
                {filteredCustomers.map((customer) => (
                  <div
                    key={customer.id}
                    className="px-3 py-2 hover:bg-gray-100 cursor-pointer border-b border-gray-100 last:border-b-0"
                    onClick={() => handleCustomerSelect(customer)}
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <div className="font-medium">{customer.name}</div>
                        <div className="text-sm text-gray-500">Code: {customer.code}</div>
                      </div>
                      {customer.code === formData.customerCode && (
                        <ChevronDown className="h-4 w-4 text-blue-600" />
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
          {formData.customerCode.length === 4 && !selectedCustomer && (
            <p className="text-xs text-red-500">Customer not found</p>
          )}
        </div>

        <div className="space-y-2">
          <Label htmlFor="customerName">Customer Name *</Label>
          <Input
            id="customerName"
            value={formData.customerName}
            onChange={(e) => setFormData({ ...formData, customerName: e.target.value })}
            placeholder="Customer name"
            readOnly={!!selectedCustomer}
            className={selectedCustomer ? 'bg-gray-100 cursor-not-allowed' : ''}
            required
          />
          {selectedCustomer && (
            <p className="text-xs text-blue-600">
              ✅ Customer from database
            </p>
          )}
        </div>
      </div>

      {/* Customer's warehouse info */}
      {selectedCustomer && selectedCustomer.destination_warehouse && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
          <p className="text-sm font-medium text-blue-900">
            📍 Customer's Destination: {warehouses.find((w: any) => w.code === selectedCustomer.destination_warehouse)?.name || selectedCustomer.destination_warehouse}
          </p>
        </div>
      )}

      {/* Trip Selection (only for admin/warehouse) */}
      {showTripSelection && (
        <>
          <div className="space-y-2">
            <Label htmlFor="trip">Trip *</Label>
            <Select value={formData.tripName} onValueChange={handleTripChange} required>
              <SelectTrigger>
                <SelectValue placeholder="Select trip" />
              </SelectTrigger>
              <SelectContent>
                {getAvailableTrips().map((trip: any) => (
                  <SelectItem key={trip.name} value={trip.name}>
                    {trip.name} - {trip.originWarehouse || trip.origin} → {trip.destinationWarehouse || trip.destination}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-xs text-gray-500">Only trips with "Planning" status are available</p>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Origin Warehouse</Label>
              <Input
                value={formData.originWarehouse}
                readOnly
                className="bg-gray-100 cursor-not-allowed"
                placeholder="Auto-filled from trip"
              />
            </div>
            <div className="space-y-2">
              <Label>Destination Warehouse</Label>
              <Input
                value={formData.destinationWarehouse}
                readOnly
                className="bg-gray-100 cursor-not-allowed"
                placeholder="Auto-filled from trip"
              />
            </div>
          </div>
        </>
      )}

      {/* Freight Method (for suppliers without trip selection) */}
      {!showTripSelection && (
        <div className="space-y-2">
          <Label htmlFor="freightMethod">Freight Method *</Label>
          <Select
            value={formData.freightMethod}
            onValueChange={(value) => setFormData({ ...formData, freightMethod: value as 'Air' | 'Sea' })}
            required
          >
            <SelectTrigger>
              <SelectValue placeholder="Select freight method" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="Air">✈️ Air Freight</SelectItem>
              <SelectItem value="Sea">🚢 Sea Freight</SelectItem>
            </SelectContent>
          </Select>
        </div>
      )}

      {/* Items Section */}
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <Label className="text-lg font-semibold">Items in This Shipment</Label>
          <Button type="button" onClick={handleAddItem} size="sm" variant="outline">
            <Plus className="h-4 w-4 mr-2" />
            Add Item
          </Button>
        </div>

        <div className="space-y-4">
          {items.map((item, index) => (
            <Card key={item.id} className="border-2">
              <CardContent className="pt-4">
                <div className="flex items-start justify-between mb-3">
                  <Badge variant="secondary" className="font-semibold">
                    Item {index + 1}
                  </Badge>
                  {items.length > 1 && (
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => handleRemoveItem(item.id)}
                      className="text-red-500 hover:text-red-700 hover:bg-red-50"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  )}
                </div>

                <div className="grid gap-4">
                  {/* Description */}
                  <div className="space-y-2">
                    <Label htmlFor={`desc-${item.id}`}>Description *</Label>
                    <Input
                      id={`desc-${item.id}`}
                      value={item.description}
                      onChange={(e) => handleUpdateItem(item.id, 'description', e.target.value)}
                      placeholder="e.g., Electronics - Laptops"
                      required
                    />
                  </div>

                  {/* Quantity & Unit */}
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor={`qty-${item.id}`}>Quantity</Label>
                      <Input
                        id={`qty-${item.id}`}
                        type="number"
                        value={item.quantity}
                        onChange={(e) => handleUpdateItem(item.id, 'quantity', e.target.value)}
                        placeholder="0"
                        min="0"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor={`unit-${item.id}`}>Unit</Label>
                      <Select
                        value={item.quantityUnit}
                        onValueChange={(value) => handleUpdateItem(item.id, 'quantityUnit', value)}
                      >
                        <SelectTrigger id={`unit-${item.id}`}>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="pcs">pcs</SelectItem>
                          <SelectItem value="kg">kg</SelectItem>
                          <SelectItem value="boxes">boxes</SelectItem>
                          <SelectItem value="sets">sets</SelectItem>
                          <SelectItem value="cartons">cartons</SelectItem>
                          <SelectItem value="pallets">pallets</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  {/* Weight, Volume, Boxes */}
                  <div className="grid grid-cols-3 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor={`weight-${item.id}`}>Weight (kg)</Label>
                      <Input
                        id={`weight-${item.id}`}
                        type="number"
                        step="any"
                        value={item.weight}
                        onChange={(e) => handleUpdateItem(item.id, 'weight', e.target.value)}
                        placeholder="0.0"
                        min="0"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor={`volume-${item.id}`}>Volume (CBM)</Label>
                      <Input
                        id={`volume-${item.id}`}
                        type="number"
                        step="any"
                        value={item.volume}
                        onChange={(e) => handleUpdateItem(item.id, 'volume', e.target.value)}
                        placeholder="0.00"
                        min="0"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor={`boxes-${item.id}`}>Boxes</Label>
                      <Input
                        id={`boxes-${item.id}`}
                        type="number"
                        value={item.boxes}
                        onChange={(e) => handleUpdateItem(item.id, 'boxes', e.target.value)}
                        placeholder="0"
                        min="0"
                      />
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>

      {/* Totals Display */}
      <Card className="bg-gradient-to-r from-blue-50 to-indigo-50 border-2 border-blue-200">
        <CardContent className="pt-4">
          <div className="flex items-center justify-between">
            <Label className="text-lg font-bold text-gray-900">Totals (Auto-calculated)</Label>
            <div className="flex items-center gap-6 text-sm font-semibold">
              <div className="text-gray-700">
                <span className="text-gray-500">Weight:</span> {totals.weight.toFixed(2)} kg
              </div>
              <div className="text-gray-700">
                <span className="text-gray-500">Volume:</span> {totals.volume.toFixed(3)} CBM
              </div>
              <div className="text-gray-700">
                <span className="text-gray-500">Boxes:</span> {totals.boxes}
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Notes */}
      <div className="space-y-2">
        <Label htmlFor="notes">Additional Notes</Label>
        <Textarea
          id="notes"
          value={formData.notes}
          onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
          placeholder="Optional notes about the shipment..."
          rows={3}
        />
      </div>

      {/* Submit Button */}
      <div className="flex justify-end gap-2 pt-4 border-t">
        <Button type="submit" disabled={isSubmitting}>
          {isSubmitting 
            ? (initialData ? 'Updating...' : 'Creating...') 
            : (initialData ? 'Update Shipment' : 'Create Shipment')}
        </Button>
      </div>
    </form>
  );
}
